<template>
  <div class="dashboard" v-loading="loading">
    <h2 class="page-title">仪表盘</h2>

    <el-row :gutter="16" class="stat-row">
      <el-col v-for="card in cards" :key="card.key" :xs="12" :sm="12" :md="6">
        <el-card class="stat-card" shadow="hover">
          <div class="stat-card-body">
            <div class="stat-card-icon" :style="{ background: card.bg }">
              <el-icon :size="22"><component :is="card.icon" /></el-icon>
            </div>
            <div class="stat-card-text">
              <div class="stat-card-label">{{ card.label }}</div>
              <div class="stat-card-value">{{ card.value }}</div>
              <div v-if="card.sub" class="stat-card-sub">{{ card.sub }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="chart-row">
      <el-col :xs="24" :md="12">
        <el-card shadow="hover" class="panel">
          <template #header>
            <div class="panel-header">
              <span>账号在线状态</span>
              <span class="panel-meta">{{ onlineCount }} / {{ totalAccounts }} 在线</span>
            </div>
          </template>
          <div class="ratio-row">
            <span class="badge badge-online">在线 {{ onlineCount }}</span>
            <span class="badge badge-offline">离线 {{ offlineCount }}</span>
          </div>
          <el-progress
            :percentage="onlinePct"
            :stroke-width="14"
            :status="onlinePct === 100 ? 'success' : onlinePct === 0 ? 'exception' : ''"
          />
          <div class="ratio-meta">在线比例 {{ onlinePct }}%</div>
        </el-card>
      </el-col>

      <el-col :xs="24" :md="12">
        <el-card shadow="hover" class="panel">
          <template #header>
            <div class="panel-header">
              <span>订阅动作类型分布</span>
              <span class="panel-meta">共 {{ totalSubscriptions }} 条</span>
            </div>
          </template>
          <el-empty v-if="totalSubscriptions === 0" description="尚无订阅" :image-size="60" />
          <div v-else class="bar-list">
            <div v-for="b in replyBreakdown" :key="b.label" class="bar-item">
              <div class="bar-label">{{ b.label }}</div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: b.pct + '%', background: b.color }" />
              </div>
              <div class="bar-value">{{ b.count }}</div>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="16" class="chart-row">
      <el-col :xs="24" :md="12">
        <el-card shadow="hover" class="panel">
          <template #header>
            <div class="panel-header">
              <span>订阅消息类型分布</span>
              <span class="panel-meta">按订阅命中条目计</span>
            </div>
          </template>
          <el-empty v-if="totalSubscriptions === 0" description="尚无订阅" :image-size="60" />
          <div v-else class="bar-list">
            <div v-for="b in messageTypeBreakdown" :key="b.key" class="bar-item">
              <div class="bar-label">{{ b.label }}</div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: b.pct + '%', background: '#5570ff' }" />
              </div>
              <div class="bar-value">{{ b.count }}</div>
            </div>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="24" :md="12">
        <el-card shadow="hover" class="panel">
          <template #header>
            <div class="panel-header">
              <span>账号列表</span>
              <span class="panel-meta">仅展示前 6 条</span>
            </div>
          </template>
          <el-empty v-if="accounts.length === 0" description="尚无账号" :image-size="60" />
          <div v-else class="account-list">
            <div v-for="acc in accounts.slice(0, 6)" :key="acc.wcId" class="account-item">
              <el-avatar shape="square" :src="acc.smallHeadImgUrl" :size="36" />
              <div class="account-meta">
                <div class="account-name">{{ acc.nickName || acc.wAccount || acc.wcId }}</div>
                <div class="account-sub">{{ acc.wAccount }}</div>
              </div>
              <el-tag :type="acc.online ? 'success' : 'info'" size="small">
                {{ acc.online ? '在线' : '离线' }}
              </el-tag>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, onMounted, ref } from "vue";
import { ChatDotRound, Platform as PlatformIcon, Promotion, Checked } from "@element-plus/icons-vue";
import {
  getWechatAccounts,
  getPlatforms,
  getTasks,
  getSubscriptions,
} from "../api/http";
import {
  ReplyTypesDict,
  type Platform as PlatformDTO,
  type SubscriptionRecord,
  type Task,
  type WechatAccount,
} from "../api/models";
import { messageTypesDict } from "../api/util";

const accounts = ref<WechatAccount[]>([]);
const platforms = ref<PlatformDTO[]>([]);
const tasks = ref<Task[]>([]);
const subscriptions = ref<SubscriptionRecord[]>([]);
const loading = ref(true);

const totalAccounts = computed(() => accounts.value.length);
const onlineCount = computed(() => accounts.value.filter((a) => a.online).length);
const offlineCount = computed(() => totalAccounts.value - onlineCount.value);
const onlinePct = computed(() =>
  totalAccounts.value > 0 ? Math.round((onlineCount.value / totalAccounts.value) * 100) : 0
);

const totalSubscriptions = computed(() => subscriptions.value.length);

const cards = computed(() => [
  {
    key: "wechat",
    label: "微信账号",
    value: totalAccounts.value,
    sub: `在线 ${onlineCount.value}`,
    icon: markRaw(ChatDotRound),
    bg: "linear-gradient(135deg, #5570ff, #67aaff)",
  },
  {
    key: "platform",
    label: "平台数",
    value: platforms.value.length,
    icon: markRaw(PlatformIcon),
    bg: "linear-gradient(135deg, #14c2ad, #4ad8b3)",
  },
  {
    key: "task",
    label: "定时任务",
    value: tasks.value.length,
    icon: markRaw(Promotion),
    bg: "linear-gradient(135deg, #ff8a3c, #ffc14a)",
  },
  {
    key: "subscription",
    label: "订阅规则",
    value: totalSubscriptions.value,
    icon: markRaw(Checked),
    bg: "linear-gradient(135deg, #b074ff, #d77fff)",
  },
]);

const replyColors = ["#5570ff", "#14c2ad", "#ff8a3c", "#b074ff"];

const replyBreakdown = computed(() => {
  const counts = ReplyTypesDict.map(() => 0);
  subscriptions.value.forEach((s) => {
    if (typeof s.replyType === "number" && counts[s.replyType] !== undefined) counts[s.replyType]++;
  });
  const max = Math.max(...counts, 1);
  return ReplyTypesDict.map((label, idx) => ({
    label,
    count: counts[idx],
    pct: counts[idx] === 0 ? 0 : Math.max(4, Math.round((counts[idx] / max) * 100)),
    color: replyColors[idx % replyColors.length],
  }));
});

const messageTypeBreakdown = computed(() => {
  const counts: Record<string, number> = {};
  Object.keys(messageTypesDict).forEach((k) => {
    counts[k] = 0;
  });
  subscriptions.value.forEach((s) => {
    (s.messageTypes || []).forEach((m) => {
      if (counts[m] !== undefined) counts[m]++;
    });
  });
  const entries = Object.entries(counts);
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return entries.map(([k, count]) => ({
    key: k,
    label: messageTypesDict[k] || k,
    count,
    pct: count === 0 ? 0 : Math.max(4, Math.round((count / max) * 100)),
  }));
});

onMounted(async () => {
  loading.value = true;
  try {
    const [a, p, t, s] = await Promise.all([
      getWechatAccounts().catch(() => null),
      getPlatforms().catch(() => null),
      getTasks().catch(() => null),
      getSubscriptions().catch(() => null),
    ]);
    if (a?.successful) accounts.value = a.result;
    if (p?.successful) platforms.value = p.result;
    if (t?.successful) tasks.value = t.result;
    if (s?.successful) subscriptions.value = s.result;
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
.dashboard {
  padding: 4px 0 32px;
}

.page-title {
  margin: 0 0 16px;
  font-size: 22px;
  color: #303133;
}

.stat-row {
  margin-bottom: 16px;
}

.stat-card-body {
  display: flex;
  align-items: center;
  gap: 14px;
}

.stat-card-icon {
  flex: 0 0 auto;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.stat-card-text {
  min-width: 0;
}

.stat-card-label {
  font-size: 13px;
  color: #909399;
}

.stat-card-value {
  font-size: 26px;
  font-weight: 600;
  color: #303133;
  line-height: 1.2;
}

.stat-card-sub {
  font-size: 12px;
  color: #67c23a;
  margin-top: 2px;
}

.chart-row {
  margin-top: 0;
  margin-bottom: 16px;
}

.panel {
  height: 100%;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  color: #303133;
}

.panel-meta {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
}

.ratio-row {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}

.badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
}

.badge-online {
  background: rgba(103, 194, 58, 0.12);
  color: #67c23a;
}

.badge-offline {
  background: rgba(144, 147, 153, 0.12);
  color: #909399;
}

.ratio-meta {
  margin-top: 10px;
  color: #606266;
  font-size: 13px;
}

.bar-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bar-item {
  display: grid;
  grid-template-columns: 110px 1fr 40px;
  align-items: center;
  gap: 10px;
}

.bar-label {
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.bar-track {
  height: 10px;
  background: #f0f2f5;
  border-radius: 5px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 5px;
  transition: width 0.3s ease;
}

.bar-value {
  font-size: 13px;
  color: #303133;
  text-align: right;
}

.account-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.account-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.account-meta {
  flex: 1;
  min-width: 0;
}

.account-name {
  font-size: 14px;
  color: #303133;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.account-sub {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 768px) {
  .page-title {
    font-size: 18px;
  }

  .stat-card-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
  }

  .stat-card-value {
    font-size: 22px;
  }

  .bar-item {
    grid-template-columns: 80px 1fr 30px;
    gap: 8px;
  }

  .stat-row .el-col,
  .chart-row .el-col {
    margin-bottom: 12px;
  }
}
</style>
